import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { inventoryApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiCamera,
  FiZap,
  FiSearch,
  FiBox,
  FiTruck,
  FiUsers,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiAlertTriangle,
  FiCopy,
  FiRefreshCw,
  FiCheck,
  FiArrowRight,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiLayers,
  FiRadio,
} from "react-icons/fi";
import { toast } from "react-toastify";

// Pleasant audio beep generator via Web Audio API on successful scan
const playSuccessBeep = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12); // E6 note

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.13);
  } catch (e) {
    // Audio might be blocked by browser policy until user interacts
  }
};

const ScanPage = () => {
  const { isSuperAdmin } = useAuth();

  // Mode: 'device' (Barcode Gun / Keyboard Wedge) | 'camera' (Webcam / Mobile Camera)
  const [scanMode, setScanMode] = useState("device");

  // Input states
  const [barcodeInput, setBarcodeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [itemData, setItemData] = useState(null);
  const [notFoundCode, setNotFoundCode] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [copiedField, setCopiedField] = useState(null);

  // Camera states
  const [cameraActive, setCameraActive] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [detectedBarcode, setDetectedBarcode] = useState(null);
  const [candidateBarcodes, setCandidateBarcodes] = useState([]);

  const inputRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isScanningPausedRef = useRef(false);
  const scannerContainerId = "barcode-camera-reader";

  // Auto-focus input on device mode
  useEffect(() => {
    if (scanMode === "device") {
      const timer = setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scanMode]);

  // Main lookup function by scanned code (Serial Number, SKU, Part Number)
  const lookupItem = useCallback(async (code) => {
    if (!code || !code.trim()) return;
    const cleanCode = code.trim();

    try {
      setLoading(true);
      setNotFoundCode(null);

      const res = await inventoryApi.scanLookup(cleanCode);
      const found = res.data;

      if (found) {
        setItemData(found);
        setNotFoundCode(null);
        playSuccessBeep();
        toast.success(`Found item: ${found.item_name}`);

        // Update recent scans list (keep last 6)
        setRecentScans((prev) => {
          const filtered = prev.filter((s) => s.code !== cleanCode);
          return [
            {
              code: cleanCode,
              name: found.item_name,
              status: found.item_status,
              time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            },
            ...filtered,
          ].slice(0, 6);
        });
      } else {
        setItemData(null);
        setNotFoundCode(cleanCode);
        toast.warning(`No item found matching: ${cleanCode}`);
      }
    } catch (err) {
      setItemData(null);
      setNotFoundCode(cleanCode);
      const msg = err.response?.data?.message || err.message || "Item not found in database";
      toast.error(msg);
    } finally {
      setLoading(false);
      setBarcodeInput("");
      if (inputRef.current) inputRef.current.focus();
    }
  }, []);

  // Handle Device Barcode Gun Input (Enter key or form submit)
  const handleDeviceSubmit = (e) => {
    if (e) e.preventDefault();
    if (barcodeInput.trim()) {
      lookupItem(barcodeInput.trim());
    }
  };

  // Check if running in a secure context (HTTPS or localhost)
  const isSecureContext =
    typeof window !== "undefined" &&
    (window.isSecureContext ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  // Setup / Teardown Camera Scanner
  const startCamera = useCallback(
    async (cameraId) => {
      setCameraError("");
      setDetectedBarcode(null);
      setCandidateBarcodes([]);
      isScanningPausedRef.current = false;

      if (!isSecureContext) {
        setCameraError("INSECURE_CONTEXT");
        return;
      }

      try {
        if (html5QrCodeRef.current) {
          try {
            if (html5QrCodeRef.current.isScanning) {
              await html5QrCodeRef.current.stop();
            }
          } catch (e) {
            // ignore stop error
          }
          try {
            html5QrCodeRef.current.clear();
          } catch (e) {
            // ignore clear error
          }
        }

        const html5QrCode = new Html5Qrcode(scannerContainerId);
        html5QrCodeRef.current = html5QrCode;

        // Use a wide rectangular scanning box to isolate individual 1D barcodes and avoid capturing adjacent barcodes
        const config = {
          fps: 15,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const width = Math.min(viewfinderWidth * 0.88, 320);
            const height = Math.min(viewfinderHeight * 0.45, 150);
            return { width: Math.max(width, 220), height: Math.max(height, 100) };
          },
          aspectRatio: 1.0,
        };

        const cameraConfig = cameraId
          ? { deviceId: { exact: cameraId } }
          : { facingMode: "environment" };

        await html5QrCode.start(
          cameraConfig,
          config,
          (decodedText) => {
            // Check if already paused/locked to ignore any other barcodes in the frame
            if (!decodedText || isScanningPausedRef.current) return;
            const clean = decodedText.trim();
            if (!clean) return;

            // Immediately lock scanning so further frames or adjacent barcodes are ignored
            isScanningPausedRef.current = true;

            // Optional haptic vibration feedback on mobile devices
            try {
              if (typeof navigator !== "undefined" && navigator.vibrate) {
                navigator.vibrate(50);
              }
            } catch (e) {}

            // Pause camera video scanner stream
            try {
              if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
                html5QrCodeRef.current.pause(true);
              }
            } catch (e) {}

            // Store the detected barcode and add to candidate list for user confirmation
            setDetectedBarcode(clean);
            setCandidateBarcodes((prev) => {
              if (prev.includes(clean)) return prev;
              return [clean, ...prev].slice(0, 4);
            });
          },
          () => {
            // frame scan parse failure, ignore frames without barcode
          }
        );

        setCameraActive(true);

        // Fetch available cameras safely after permission is granted
        try {
          const cameras = await Html5Qrcode.getCameras();
          if (cameras && cameras.length > 0) {
            setAvailableCameras(cameras);
            if (!cameraId) {
              const backCam = cameras.find((c) =>
                /back|rear|environment|primary/i.test(c.label)
              );
              setSelectedCameraId(backCam ? backCam.id : cameras[0].id);
            }
          }
        } catch (camErr) {
          console.warn("Could not enumerate camera devices:", camErr);
        }
      } catch (err) {
        console.error("Camera start error:", err);
        const errMsg = (err?.message || String(err)).toLowerCase();
        if (
          err.name === "NotAllowedError" ||
          errMsg.includes("permission") ||
          errMsg.includes("denied") ||
          errMsg.includes("notallowederror")
        ) {
          setCameraError("PERMISSION_DENIED");
        } else {
          setCameraError(err.message || "Failed to start camera. Please verify permissions.");
        }
        setCameraActive(false);
      }
    },
    [isSecureContext]
  );

  // Manual Confirmation Handler: ONLY sends the confirmed barcode to backend
  const handleConfirmScan = useCallback(() => {
    if (!detectedBarcode) return;
    const targetCode = detectedBarcode;

    // Send only the chosen confirmed barcode to backend
    lookupItem(targetCode);

    // Reset detection state and resume camera scanning
    setDetectedBarcode(null);
    setCandidateBarcodes([]);
    isScanningPausedRef.current = false;
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.resume();
      }
    } catch (e) {}
  }, [detectedBarcode, lookupItem]);

  // Rescan / Cancel Handler: Discards detected barcode without calling backend
  const handleCancelScan = useCallback(() => {
    setDetectedBarcode(null);
    setCandidateBarcodes([]);
    isScanningPausedRef.current = false;
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.resume();
      }
    } catch (e) {}
  }, []);

  const stopCamera = useCallback(async () => {
    isScanningPausedRef.current = false;
    setDetectedBarcode(null);
    setCandidateBarcodes([]);
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Camera stop error:", err);
      }
      html5QrCodeRef.current = null;
    }
    setCameraActive(false);
  }, []);

  // Handle camera mode change
  useEffect(() => {
    if (scanMode === "camera") {
      if (!isSecureContext) {
        setCameraError("INSECURE_CONTEXT");
        return;
      }
      startCamera(selectedCameraId || undefined);
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [scanMode, startCamera, stopCamera, isSecureContext]);

  // Switch camera device
  const handleCameraChange = (e) => {
    const newId = e.target.value;
    setSelectedCameraId(newId);
    if (cameraActive) {
      startCamera(newId);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.info(`Copied ${fieldName} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div
          style={{
            maxWidth: 440,
            margin: "0 auto",
            backgroundColor: "#fff",
            padding: "36px 28px",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <FiAlertCircle size={44} color="#DC2626" style={{ marginBottom: 14 }} />
          <h2 style={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--navy)", marginBottom: 8 }}>
            Access Restricted
          </h2>
          <p style={{ color: "var(--slate-500)", fontSize: "0.88rem", lineHeight: 1.6 }}>
            The Barcode & Camera Scanner module is strictly restricted to <strong>Super Admin</strong> only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ paddingBottom: 40 }}>
      {/* Page Title & Breadcrumb Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                backgroundColor: "var(--primary-light, #EEF2FF)",
                color: "var(--primary, #064E3B)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiZap size={20} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                  Barcode & Serial Scanner
                </h2>
                <span
                  style={{
                    fontSize: "0.74rem",
                    fontWeight: 800,
                    backgroundColor: "rgba(16, 185, 129, 0.15)",
                    color: "var(--primary)",
                    padding: "2px 8px",
                    borderRadius: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Super Admin Only
                </span>
              </div>
              <div style={{ fontSize: "0.82rem", color: "var(--slate-500)", marginTop: 2 }}>
                Scan serial number or barcode to fetch complete item lifecycle, vendor, client & stock details.
              </div>
            </div>
          </div>
        </div>

        {/* Scan Mode Toggle: Hardware Gun Device vs Camera */}
        <div
          style={{
            display: "inline-flex",
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <button
            type="button"
            onClick={() => setScanMode("device")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: "var(--radius-md)",
              border: "none",
              backgroundColor: scanMode === "device" ? "var(--primary)" : "transparent",
              color: scanMode === "device" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.86rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <FiRadio size={16} /> Device Scanner (Gun / HID)
          </button>

          <button
            type="button"
            onClick={() => setScanMode("camera")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: "var(--radius-md)",
              border: "none",
              backgroundColor: scanMode === "camera" ? "var(--primary)" : "transparent",
              color: scanMode === "camera" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.86rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <FiCamera size={16} /> Mobile / Web Cam
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SCANNER INPUT CARD (DEVICE OR CAMERA)                                    */}
      {/* ========================================================================= */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "var(--radius-lg)",
          padding: 24,
          border: "1px solid var(--slate-200)",
          boxShadow: "var(--shadow-sm)",
          marginBottom: 24,
        }}
      >
        {scanMode === "device" ? (
          /* OPTION 1: DEVICE BARCODE SCANNER (GUN / USB / BLUETOOTH) */
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "var(--success, #10B981)",
                    boxShadow: "0 0 8px #10B981",
                  }}
                />
                <span style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--navy)" }}>
                  Hardware Barcode Scanner Active
                </span>
                <span style={{ fontSize: "0.78rem", color: "var(--slate-400)" }}>
                  (Scan with barcode gun or type manually)
                </span>
              </div>

              {loading && (
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", color: "var(--primary)", fontWeight: 700 }}>
                  <FiRefreshCw size={14} className="spin" /> Searching database...
                </span>
              )}
            </div>

            <form onSubmit={handleDeviceSubmit}>
              <div style={{ position: "relative" }}>
                <FiZap
                  size={20}
                  style={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--primary)",
                  }}
                />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Scan barcode with gun, or enter Serial Number / SKU and hit Enter..."
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "14px 140px 14px 48px",
                    borderRadius: "var(--radius-md)",
                    border: "2px solid var(--primary)",
                    fontSize: "1rem",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    outline: "none",
                    boxSizing: "border-box",
                    backgroundColor: "#FAFBFD",
                    boxShadow: "0 0 0 3px rgba(6, 78, 59, 0.08)",
                  }}
                />
                <button
                  type="submit"
                  disabled={loading || !barcodeInput.trim()}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "9px 20px",
                    borderRadius: 8,
                    backgroundColor: "var(--primary)",
                    color: "#fff",
                    border: "none",
                    fontWeight: 700,
                    fontSize: "0.86rem",
                    cursor: loading || !barcodeInput.trim() ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FiSearch size={15} /> Lookup
                </button>
              </div>
            </form>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.78rem", color: "var(--slate-500)" }}>
                💡 <strong>Scanner Gun Tip:</strong> The scanner automatically types the serial number and presses Enter. Keep your cursor in the box or simply scan.
              </span>
            </div>
          </div>
        ) : (
          /* OPTION 2: CAMERA SCANNER (MOBILE / WEBCAM) */
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: cameraActive ? "var(--success)" : "var(--slate-400)",
                    boxShadow: cameraActive ? "0 0 8px #10B981" : "none",
                  }}
                />
                <span style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--navy)" }}>
                  Camera Scanner {cameraActive ? "Active" : "Stopped"}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {availableCameras.length > 1 && (
                  <select
                    value={selectedCameraId}
                    onChange={handleCameraChange}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                    }}
                  >
                    {availableCameras.map((cam) => (
                      <option key={cam.id} value={cam.id}>
                        📷 {cam.label || `Camera ${cam.id.slice(0, 6)}`}
                      </option>
                    ))}
                  </select>
                )}

                {cameraActive ? (
                  <button
                    type="button"
                    onClick={stopCamera}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 6,
                      border: "1px solid var(--danger)",
                      backgroundColor: "var(--danger-bg, #FEF2F2)",
                      color: "var(--danger)",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Stop Camera
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => startCamera(selectedCameraId)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: "var(--primary)",
                      color: "#fff",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Start Camera
                  </button>
                )}
              </div>
            </div>

            {cameraError && (
              <div
                style={{
                  borderRadius: 10,
                  border: "1px solid #FECACA",
                  backgroundColor: "#FEF2F2",
                  padding: "16px 20px",
                  marginBottom: 18,
                  fontSize: "0.88rem",
                  color: "#991B1B",
                }}
              >
                {cameraError === "INSECURE_CONTEXT" ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: "0.95rem", marginBottom: 6 }}>
                      <FiAlertTriangle size={20} color="#DC2626" />
                      <span>🔒 Camera Access Blocked: HTTPS Required on Mobile</span>
                    </div>
                    <p style={{ margin: "4px 0 10px 0", color: "#475569", lineHeight: 1.5 }}>
                      Mobile browsers (Chrome, Safari, Edge) <strong>strictly block camera access over plain HTTP</strong> (e.g. <code>http://192.168.x.x:3001</code>). Web security standards require a Secure Context (<strong>HTTPS</strong> or <strong>localhost</strong>).
                    </p>
                    <div
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: 8,
                        padding: 12,
                        border: "1px solid #FEE2E2",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        color: "#334155",
                        fontSize: "0.82rem",
                      }}
                    >
                      <div>
                        <strong>Option 1 (Quick Fix in Android Chrome):</strong>
                        <ol style={{ margin: "4px 0 0 18px", padding: 0, lineHeight: 1.6 }}>
                          <li>On mobile Chrome, open URL: <code style={{ backgroundColor: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>chrome://flags/#unsafely-treat-insecure-origin-as-secure</code></li>
                          <li>In the text box, paste your current URL (e.g. <code style={{ backgroundColor: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>{window.location.origin}</code>)</li>
                          <li>Change dropdown to <strong>Enabled</strong> and tap <strong>Relaunch</strong>.</li>
                        </ol>
                      </div>
                      <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: 8 }}>
                        <strong>Option 2 (Enable HTTPS on UI Server):</strong>
                        <div style={{ marginTop: 4, lineHeight: 1.5 }}>
                          Add <code style={{ backgroundColor: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>HTTPS=true</code> in <code style={{ backgroundColor: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>it-ui/.env</code> and restart React (<code style={{ backgroundColor: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>npm start</code>). Then access via <code style={{ backgroundColor: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>https://...</code>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : cameraError === "PERMISSION_DENIED" ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: "0.95rem", marginBottom: 6 }}>
                      <FiAlertCircle size={20} color="#DC2626" />
                      <span>🚫 Camera Permission Denied</span>
                    </div>
                    <p style={{ margin: "4px 0 10px 0", color: "#475569", lineHeight: 1.5 }}>
                      The mobile browser blocked access to the camera. Please allow camera permissions in your browser settings:
                    </p>
                    <div
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: 8,
                        padding: 12,
                        border: "1px solid #FEE2E2",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        color: "#334155",
                        fontSize: "0.82rem",
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <strong>🤖 For Android (Chrome):</strong>
                        <div style={{ marginTop: 2, lineHeight: 1.6 }}>
                          Tap the <strong>Tune / Settings icon</strong> (left of URL address bar) ➔ Tap <strong>Permissions</strong> or <strong>Site settings</strong> ➔ Tap <strong>Camera</strong> ➔ Select <strong>Allow</strong>.
                        </div>
                      </div>
                      <div style={{ borderTop: "1px dashed #E2E8F0", paddingTop: 8 }}>
                        <strong>🍎 For iPhone / iPad (Safari):</strong>
                        <div style={{ marginTop: 2, lineHeight: 1.6 }}>
                          Tap the <strong>aA</strong> icon on the left of address bar ➔ Tap <strong>Website Settings</strong> ➔ Set <strong>Camera</strong> to <strong>Allow</strong>.
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => startCamera(selectedCameraId)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 6,
                          backgroundColor: "var(--primary)",
                          color: "#fff",
                          border: "none",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        <FiRefreshCw size={14} /> Retry Camera
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <FiAlertCircle size={18} />
                      <span>{cameraError}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => startCamera(selectedCameraId)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "5px 12px",
                        borderRadius: 6,
                        backgroundColor: "var(--primary)",
                        color: "#fff",
                        border: "none",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      <FiRefreshCw size={13} /> Retry
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Video Preview Container with dynamic Green highlight upon detection */}
            <div
              style={{
                maxWidth: 420,
                margin: "0 auto",
                borderRadius: "var(--radius-md)",
                overflow: "hidden",
                border: detectedBarcode
                  ? "3px solid #10B981"
                  : "2px dashed var(--primary)",
                boxShadow: detectedBarcode
                  ? "0 0 24px rgba(16, 185, 129, 0.45)"
                  : "none",
                backgroundColor: "#0F172A",
                minHeight: 280,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div id={scannerContainerId} style={{ width: "100%" }} />

              {/* Floating Green Detection Badge */}
              {detectedBarcode && (
                <div
                  style={{
                    position: "absolute",
                    top: 12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(16, 185, 129, 0.95)",
                    color: "#ffffff",
                    padding: "6px 14px",
                    borderRadius: 20,
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    zIndex: 10,
                    animation: "pulseGlow 2s infinite",
                  }}
                >
                  <FiCheckCircle size={16} /> Barcode Detected!
                </div>
              )}

              {!cameraActive && !cameraError && (
                <div style={{ color: "#94A3B8", textAlign: "center", padding: 20 }}>
                  <FiCamera size={36} style={{ marginBottom: 8, opacity: 0.6 }} />
                  <div>Click "Start Camera" to point camera at barcode or QR code</div>
                </div>
              )}
            </div>

            {/* MANUAL CONFIRMATION CARD (Target Locked & User Control) */}
            {detectedBarcode && (
              <div
                style={{
                  maxWidth: 420,
                  margin: "16px auto 0",
                  backgroundColor: "#ffffff",
                  borderRadius: "var(--radius-md)",
                  border: "2px solid #10B981",
                  boxShadow: "0 8px 24px rgba(16, 185, 129, 0.2)",
                  padding: "16px 18px",
                  animation: "fadeIn 0.2s ease-out",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#065F46", fontWeight: 800, fontSize: "0.92rem" }}>
                    <FiCheckCircle size={18} color="#10B981" />
                    <span>Barcode Detected — Confirm Scan</span>
                  </div>
                  <span
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: "#D1FAE5",
                      color: "#065F46",
                      padding: "2px 8px",
                      borderRadius: 12,
                    }}
                  >
                    Target Locked
                  </span>
                </div>

                {/* Display Current Selected Barcode */}
                <div
                  style={{
                    backgroundColor: "#F8FAFC",
                    border: "1.5px dashed #CBD5E1",
                    borderRadius: 8,
                    padding: "12px 14px",
                    textAlign: "center",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 4 }}>
                    Selected Target Barcode
                  </div>
                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: "1.25rem",
                      fontWeight: 800,
                      color: "var(--navy)",
                      letterSpacing: "1px",
                      wordBreak: "break-all",
                    }}
                  >
                    {detectedBarcode}
                  </div>
                </div>

                {/* Multi-Barcode Selector: If camera saw multiple barcodes, let user choose exactly which one */}
                {candidateBarcodes.length > 1 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: "0.74rem", fontWeight: 700, color: "var(--slate-600)", marginBottom: 6 }}>
                      Multiple barcodes visible. Tap to select:
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {candidateBarcodes.map((code) => {
                        const isSelected = code === detectedBarcode;
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => setDetectedBarcode(code)}
                            style={{
                              padding: "4px 10px",
                              borderRadius: 6,
                              fontSize: "0.76rem",
                              fontFamily: "monospace",
                              fontWeight: 700,
                              cursor: "pointer",
                              border: isSelected ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                              backgroundColor: isSelected ? "var(--primary-light)" : "#fff",
                              color: isSelected ? "var(--primary)" : "var(--slate-700)",
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {isSelected && <FiCheck size={12} />}
                            <span>{code}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", marginBottom: 14, textAlign: "center" }}>
                  Only this barcode will be sent to the backend. Other barcodes in view are ignored.
                </div>

                {/* Action Buttons: Confirm & Send vs Rescan */}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    onClick={handleConfirmScan}
                    disabled={loading}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "10px 16px",
                      borderRadius: 8,
                      backgroundColor: "var(--primary)",
                      color: "#fff",
                      border: "none",
                      fontSize: "0.9rem",
                      fontWeight: 800,
                      cursor: "pointer",
                      boxShadow: "0 4px 12px var(--accent-glow)",
                      transition: "transform 0.15s, opacity 0.15s",
                    }}
                  >
                    <FiSearch size={16} />
                    <span>Confirm & Send</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleCancelScan}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      padding: "10px 14px",
                      borderRadius: 8,
                      backgroundColor: "var(--slate-100)",
                      color: "var(--slate-700)",
                      border: "1px solid var(--slate-300)",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <FiRefreshCw size={14} />
                    <span>Rescan</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent Scans Session Chips */}
        {recentScans.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--slate-100)" }}>
            <span style={{ fontSize: "0.76rem", fontWeight: 700, color: "var(--slate-400)", textTransform: "uppercase", marginRight: 8 }}>
              Recent Scans:
            </span>
            <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {recentScans.map((scan, idx) => (
                <button
                  key={`${scan.code}-${idx}`}
                  type="button"
                  onClick={() => lookupItem(scan.code)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    borderRadius: 14,
                    border: "1px solid var(--slate-200)",
                    backgroundColor: "var(--slate-50)",
                    fontSize: "0.78rem",
                    cursor: "pointer",
                    color: "var(--navy)",
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)" }}>{scan.code}</span>
                  <span style={{ color: "var(--slate-500)", fontSize: "0.74rem" }}>({scan.name})</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* ITEM NOT FOUND ALERT                                                      */}
      {/* ========================================================================= */}
      {notFoundCode && (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "24px 28px",
            border: "1px solid #FCA5A5",
            boxShadow: "var(--shadow-sm)",
            marginBottom: 24,
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              backgroundColor: "var(--danger-bg, #FEF2F2)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FiAlertTriangle size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: "0 0 4px", fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)" }}>
              No Item Found in Database
            </h4>
            <p style={{ margin: "0 0 12px", fontSize: "0.88rem", color: "var(--slate-600)" }}>
              Scanned code: <strong style={{ fontFamily: "monospace", color: "var(--danger)" }}>{notFoundCode}</strong> does not match any serial number, SKU, or part number in the inventory.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link
                to="/purchases"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  borderRadius: 6,
                  backgroundColor: "var(--primary)",
                  color: "#fff",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Add as New Purchase <FiArrowRight size={14} />
              </Link>
              <button
                type="button"
                onClick={() => {
                  setNotFoundCode(null);
                  if (inputRef.current) inputRef.current.focus();
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Scan Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCANNED ITEM COMPLETE DETAILS CARD                                        */}
      {/* ========================================================================= */}
      {itemData && (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-md)",
            overflow: "hidden",
          }}
        >
          {/* Header Banner */}
          <div
            style={{
              padding: "20px 24px",
              backgroundColor: "#FAFBFD",
              borderBottom: "1px solid var(--slate-200)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h3 style={{ fontSize: "1.35rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                  {itemData.item_name}
                </h3>

                {/* Status Badge */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 12px",
                    borderRadius: 14,
                    fontSize: "0.78rem",
                    fontWeight: 800,
                    backgroundColor:
                      itemData.item_status === "In Stock (Available)"
                        ? "var(--success-bg, #D1FAE5)"
                        : itemData.item_status === "Returned"
                        ? "#FEF3C7"
                        : "#E0F2FE",
                    color:
                      itemData.item_status === "In Stock (Available)"
                        ? "var(--success, #059669)"
                        : itemData.item_status === "Returned"
                        ? "#92400E"
                        : "#0369A1",
                  }}
                >
                  <FiCheckCircle size={13} /> {itemData.item_status}
                </span>

                {/* Warranty Badge */}
                {itemData.warranty_status && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 14,
                      fontSize: "0.76rem",
                      fontWeight: 700,
                      backgroundColor:
                        itemData.warranty_status === "Active"
                          ? "#EEF2FF"
                          : itemData.warranty_status === "Expiring Soon"
                          ? "#FEF3C7"
                          : "var(--slate-100)",
                      color:
                        itemData.warranty_status === "Active"
                          ? "var(--primary)"
                          : itemData.warranty_status === "Expiring Soon"
                          ? "#B45309"
                          : "var(--slate-600)",
                    }}
                  >
                    <FiClock size={12} /> Warranty: {itemData.warranty_status}
                  </span>
                )}
              </div>

              {/* Category & Subcategory breadcrumb */}
              <div style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginTop: 6 }}>
                <strong>{itemData.category_name || "General"}</strong> &bull; Sub-category: <strong>{itemData.subcategory_name || "—"}</strong>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Link
                to="/stock"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontSize: "0.84rem",
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                <FiLayers size={14} /> View in Stock
              </Link>
            </div>
          </div>

          {/* Key Identifiers Grid (Serial #, SKU, Part #) */}
          <div
            style={{
              padding: "16px 24px",
              backgroundColor: "#fff",
              borderBottom: "1px solid var(--slate-100)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            {/* Serial Number */}
            <div style={{ backgroundColor: "#F8FAFC", padding: "12px 16px", borderRadius: 10, border: "1px solid var(--slate-200)" }}>
              <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                Serial Number (SN)
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: "1.05rem", fontWeight: 800, fontFamily: "monospace", color: "var(--navy)" }}>
                  {itemData.serial_number || "—"}
                </span>
                {itemData.serial_number && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(itemData.serial_number, "Serial Number")}
                    title="Copy Serial Number"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--slate-400)" }}
                  >
                    {copiedField === "Serial Number" ? <FiCheck size={16} color="var(--success)" /> : <FiCopy size={16} />}
                  </button>
                )}
              </div>
            </div>

            {/* SKU Code */}
            <div style={{ backgroundColor: "#F8FAFC", padding: "12px 16px", borderRadius: 10, border: "1px solid var(--slate-200)" }}>
              <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                SKU Barcode
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: "1.05rem", fontWeight: 800, fontFamily: "monospace", color: "var(--primary)" }}>
                  {itemData.sku || "—"}
                </span>
                {itemData.sku && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(itemData.sku, "SKU")}
                    title="Copy SKU"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--slate-400)" }}
                  >
                    {copiedField === "SKU" ? <FiCheck size={16} color="var(--success)" /> : <FiCopy size={16} />}
                  </button>
                )}
              </div>
            </div>

            {/* Part Number */}
            <div style={{ backgroundColor: "#F8FAFC", padding: "12px 16px", borderRadius: 10, border: "1px solid var(--slate-200)" }}>
              <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                Part Number (PN)
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: "1.05rem", fontWeight: 700, fontFamily: "monospace", color: "var(--slate-700)" }}>
                  {itemData.part_number || "—"}
                </span>
                {itemData.part_number && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(itemData.part_number, "Part Number")}
                    title="Copy Part Number"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--slate-400)" }}
                  >
                    {copiedField === "Part Number" ? <FiCheck size={16} color="var(--success)" /> : <FiCopy size={16} />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Complete 3-Column Lifecycle History */}
          <div
            style={{
              padding: 24,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))",
              gap: 20,
            }}
          >
            {/* PANEL 1: PROCUREMENT & VENDOR (STOCK IN) */}
            <div
              style={{
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-200)",
                padding: 18,
                backgroundColor: "#fff",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--slate-100)" }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#E0F2FE", color: "#0369A1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FiTruck size={17} />
                </div>
                <div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--navy)" }}>
                    Purchase / Vendor Info (IN)
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "var(--slate-400)" }}>
                    Supplier & Purchase Bill Details
                  </div>
                </div>
              </div>

              {itemData.vendor_name || itemData.bill_number ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: "0.85rem" }}>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Vendor / Supplier</div>
                    <div style={{ fontWeight: 800, color: "var(--navy)", marginTop: 2 }}>{itemData.vendor_name || "—"}</div>
                    {itemData.vendor_contact && (
                      <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", marginTop: 1 }}>
                        📞 {itemData.vendor_contact} {itemData.vendor_contact_person && `(${itemData.vendor_contact_person})`}
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Bill / PO Number</div>
                    <div style={{ fontWeight: 700, fontFamily: "monospace", color: "var(--primary)", marginTop: 2 }}>
                      {itemData.bill_number || "—"}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Purchase Date</div>
                      <div style={{ fontWeight: 600, color: "var(--slate-700)", marginTop: 2 }}>
                        {itemData.purchase_date || "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Purchase Cost</div>
                      <div style={{ fontWeight: 800, color: "var(--navy)", marginTop: 2 }}>
                        ${Number(itemData.price || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {itemData.purchase_user_name && (
                    <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", marginTop: 4, paddingTop: 8, borderTop: "1px dashed var(--slate-200)" }}>
                      <FiUser size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
                      Recorded by: <strong>{itemData.purchase_user_name}</strong> {itemData.purchase_user_username && `(@${itemData.purchase_user_username})`}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: "20px 0", textAlign: "center", color: "var(--slate-400)", fontSize: "0.85rem" }}>
                  No vendor purchase record linked with this item.
                </div>
              )}
            </div>

            {/* PANEL 2: SALES & CLIENT DELIVERY (STOCK OUT) */}
            <div
              style={{
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-200)",
                padding: 18,
                backgroundColor: "#fff",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--slate-100)" }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "var(--success-bg, #D1FAE5)", color: "var(--success, #059669)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FiUsers size={17} />
                </div>
                <div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--navy)" }}>
                    Sales / Client Info (OUT)
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "var(--slate-400)" }}>
                    Buyer & Sales Invoice Details
                  </div>
                </div>
              </div>

              {itemData.is_sale || itemData.client_name || itemData.invoice_number ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: "0.85rem" }}>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Client / Customer</div>
                    <div style={{ fontWeight: 800, color: "var(--navy)", marginTop: 2 }}>{itemData.client_name || "—"}</div>
                    {itemData.client_contact && (
                      <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", marginTop: 1 }}>
                        📞 {itemData.client_contact} {itemData.client_contact_person && `(${itemData.client_contact_person})`}
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Sales Invoice #</div>
                    <div style={{ fontWeight: 700, fontFamily: "monospace", color: "var(--success)", marginTop: 2 }}>
                      {itemData.invoice_number || "—"}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Sale Date</div>
                      <div style={{ fontWeight: 600, color: "var(--slate-700)", marginTop: 2 }}>
                        {itemData.sale_date || "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Selling Price</div>
                      <div style={{ fontWeight: 800, color: "var(--success)", marginTop: 2 }}>
                        ${Number(itemData.sale_price || itemData.price || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {itemData.sale_user_name && (
                    <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", marginTop: 4, paddingTop: 8, borderTop: "1px dashed var(--slate-200)" }}>
                      <FiUser size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
                      Sold by: <strong>{itemData.sale_user_name}</strong> {itemData.sale_user_username && `(@${itemData.sale_user_username})`}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: "26px 14px", textAlign: "center", backgroundColor: "var(--slate-50)", borderRadius: 8 }}>
                  <FiBox size={26} style={{ color: "var(--slate-400)", marginBottom: 6 }} />
                  <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: "0.88rem" }}>Currently In Stock</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 2 }}>
                    This item has not been sold yet and is available in inventory ready for dispatch.
                  </div>
                </div>
              )}
            </div>

            {/* PANEL 3: WARRANTY & SYSTEM TIMESTAMPS */}
            <div
              style={{
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-200)",
                padding: 18,
                backgroundColor: "#fff",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--slate-100)" }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#EDE9FE", color: "#7C3AED", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FiClock size={17} />
                </div>
                <div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--navy)" }}>
                    Warranty & Timestamps
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "var(--slate-400)" }}>
                    Coverage & Activity Audit
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: "0.85rem" }}>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Warranty Duration</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                      {itemData.warranty_start_date || "—"} to {itemData.warranty_end_date || "—"}
                    </span>
                  </div>
                  {itemData.warranty_days_remaining !== null && itemData.warranty_days_remaining !== undefined && (
                    <div style={{ fontSize: "0.78rem", color: itemData.warranty_days_remaining > 0 ? "var(--primary)" : "var(--danger)", fontWeight: 700, marginTop: 3 }}>
                      {itemData.warranty_days_remaining > 0 ? `⏳ ${itemData.warranty_days_remaining} days remaining` : "❌ Warranty expired"}
                    </div>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>System Created</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--slate-600)", marginTop: 2 }}>
                      {itemData.created_at ? new Date(itemData.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>Last Activity</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--slate-600)", marginTop: 2 }}>
                      {itemData.updated_at ? new Date(itemData.updated_at).toLocaleString() : "—"}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 8, paddingTop: 10, borderTop: "1px dashed var(--slate-200)" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setItemData(null);
                      setBarcodeInput("");
                      if (inputRef.current) inputRef.current.focus();
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: 6,
                      backgroundColor: "var(--slate-100)",
                      color: "var(--slate-700)",
                      border: "none",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <FiRefreshCw size={14} /> Scan Next Item
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanPage;
